import { Divider } from '@ui-kitten/components'
import React, {
  FunctionComponent,
  ReactElement,
  useMemo,
  useState,
} from 'react'
import {
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewProps,
} from 'react-native'
import { composeStyle } from '../helpers'
import { colors } from '../theme'
import { BtText } from './BtText'

interface TabProps extends ViewProps {
  label: string
  disabled?: boolean
}

export const Tab: FunctionComponent<TabProps> = ({
  label,
  disabled,
  ...rest
}) => <View {...rest} />

interface TabHeaderProps extends TouchableOpacityProps {
  label: string
  active: boolean
}

const TabHeader: FunctionComponent<TabHeaderProps> = ({
  active,
  label,
  style,
  ...rest
}) => {
  const titleStyle = {
    textAlign: 'center',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 22,
    color: '#335C75',
  }
  return (
    <TouchableOpacity
      style={[
        {
          borderBottomWidth: 2,
          borderBottomColor: active ? colors.accentText : 'transparent',
          paddingBottom: 10,
          marginRight: 20,
        },
        style,
      ]}
      {...rest}
    >
      <BtText
        style={composeStyle(
          titleStyle,
          rest.disabled && { opacity: 0.5 },
          active && { color: colors.accentText },
        )}
      >
        {label}
      </BtText>
    </TouchableOpacity>
  )
}

interface TabsProps {
  onTabSelect?(activeIndex: number)

  activeIndex?: number
  children: React.ReactElement<TabProps>[]
}

const Tabs: FunctionComponent<TabsProps> = ({
  children,
  onTabSelect,
  activeIndex,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const _index = activeIndex || activeIndex === 0 ? activeIndex : currentIndex

  const tabs = useMemo(
    () =>
      React.Children.toArray(children).filter(
        (child): child is ReactElement<TabProps, typeof Tab> =>
          child ? React.isValidElement<TabProps>(child) : false,
      ),
    [children],
  )

  const onPressTab = (index: number) => {
    if (onTabSelect) onTabSelect(index)
    else setCurrentIndex(index)
  }

  return (
    <>
      <View style={{ flexDirection: 'row' }}>
        {tabs.map((tab, index) => (
          <TabHeader
            key={index}
            active={_index === index}
            label={tab.props.label}
            style={tab.props.style}
            disabled={tab.props.disabled}
            onPress={() => onPressTab(index)}
            aria-selected={_index === index}
          />
        ))}
      </View>
      <Divider style={{ marginBottom: 16 }} />
      {tabs.find((_, index) => _index === index)}
    </>
  )
}

export default Tabs
